import UiLanguageSelect from "../components/UiLanguageSelect";
import { Trans, useTranslation } from "react-i18next";
// src/pages/StaffAcceptInvite.tsx
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { apiGet, apiPost } from "../api/client";
import { A } from "../styles/appleClasses";
import { Button } from "../components/Button";

type InviteInfo = {
  email: string;
  company_id: number;
  company_name?: string | null;
  is_company_admin: boolean;
  has_account: boolean;
};

type Status = "loading" | "ready" | "submitting" | "success" | "error";

type InviteError =
  | { kind: "local"; key: "staffAcceptInvite.missingToken" | "staffAcceptInvite.unavailable" | "staffAcceptInvite.acceptFailed" | "staffAcceptInvite.invalidToken" | "staffAcceptInvite.invalidOrExpiredToken" | "staffAcceptInvite.emailOtherCompany" | "register.passwordMinimum" | "accountInlays.errors.staffProRequired" | "accountInlays.errors.tooManyRequests" }
  | { kind: "raw"; value: React.ReactNode };

export default function StaffAcceptInvite() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [status, setStatus] = useState<Status>("loading");
  const [invite, setInvite] = useState<InviteInfo | null>(null);
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<InviteError | null>(null);

  // 1) Load invite info from backend
  useEffect(() => {
    if (!token) {
      setStatus("error");
      setError({ kind: "local", key: "staffAcceptInvite.missingToken" });
      return;
    }

    apiGet<InviteInfo>(`/auth/invite-info?token=${encodeURIComponent(token)}`)
      .then((data) => {
        setInvite(data);
        if (data?.email) {
          const defaultName = data.email.split("@")[0];
          setFullName(defaultName);
        }
        setStatus("ready");
      })
      .catch((err: any) => {
        console.error(err);
        const detail = err?.response?.data?.detail;
        const msg = Array.isArray(detail) ? detail.join(", ") : detail;
        if (detail === "Invalid invite token.") setError({ kind: "local", key: "staffAcceptInvite.invalidToken" });
        else if (detail === "Invalid or expired invite token.") setError({ kind: "local", key: "staffAcceptInvite.invalidOrExpiredToken" });
        else setError(msg ? { kind: "raw", value: msg } : { kind: "local", key: "staffAcceptInvite.unavailable" });
        setStatus("error");
      });
  }, [token]);

  // 2) Submit accept-invite
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!invite) return;

    setStatus("submitting");
    setError(null);

    try {
      const body: any = { token };

      // For brand-new users, backend requires password (and we send full_name)
      if (!invite.has_account) {
        body.password = password;
        body.full_name = fullName;
      }

      const res = await apiPost<{
        access_token: string;
        token_type: string;
        company_id: number;
        company_name?: string | null;
      }>("/auth/accept-invite", body);

      if (res.access_token) {
        localStorage.setItem("access_token", res.access_token);
        sessionStorage.setItem("access_token", res.access_token);
      }

      setStatus("success");
      navigate(`/company/${res.company_id}/dashboard`, { replace: true });
    } catch (err: any) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.join(", ") : detail;
      const responseStatus = err?.response?.status;
      if (responseStatus === 400 && detail === "Invalid invite token.") setError({ kind: "local", key: "staffAcceptInvite.invalidToken" });
      else if (responseStatus === 400 && detail === "Invalid or expired invite token.") setError({ kind: "local", key: "staffAcceptInvite.invalidOrExpiredToken" });
      else if (responseStatus === 400 && detail === "Password must be at least 8 characters.") setError({ kind: "local", key: "register.passwordMinimum" });
      else if (responseStatus === 400 && detail === "This email is already used for another company.") setError({ kind: "local", key: "staffAcceptInvite.emailOtherCompany" });
      else if (responseStatus === 403 && detail === "Staff access requires an active Pro subscription.") setError({ kind: "local", key: "accountInlays.errors.staffProRequired" });
      else if (responseStatus === 429 && detail === "Too many requests. Please try again later.") setError({ kind: "local", key: "accountInlays.errors.tooManyRequests" });
      else setError(msg ? { kind: "raw", value: msg } : { kind: "local", key: "staffAcceptInvite.acceptFailed" });
      setStatus("ready");
    }
  };

  const companyName = invite?.company_name || t("staffAcceptInvite.companyFallback");
  const errorText = error?.kind === "raw" ? error.value : error ? t(error.key) : null;
  const isExistingUser = !!invite?.has_account;

  const backLink = "/login";
  const labelCls = `block mb-1 ${A.label}`;
  const alertBase = `rounded-2xl border px-4 py-3 ${A.body} text-center`;

  // ---------- RENDER ----------
  if (status === "loading") {
    return (
      <div className={`min-h-screen bg-gray-50 ${A.body} flex items-center justify-center`}>
        <div className="flex flex-col items-center gap-4">
          <UiLanguageSelect />
          <div className={A.label}>{t("staffAcceptInvite.loading")}</div>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className={`min-h-screen bg-gray-50 ${A.body} flex items-center justify-center px-4`}>
        <div className={`${A.card} w-full max-w-md p-6 text-center`}>
          <div className="mb-4 flex justify-end"><UiLanguageSelect /></div>
          <h1 className={A.title}>{t("staffAcceptInvite.problem")}</h1>
          <p className={`mt-2 ${A.label}`}>
            {errorText || t("staffAcceptInvite.invalidLink")}
          </p>

          <div className="mt-4">
            <Button
              type="button"
              variant="primary"
              minWidth="auto"
              className="px-5"
              onClick={() => navigate(backLink)}
            >
              {t("verifyEmail.goToLogin")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gray-50 ${A.body} flex flex-col items-center pt-6 px-4`}>
      <div className="w-full max-w-md">
        <div className="mb-4 flex justify-end"><UiLanguageSelect /></div>
        {/* Header */}
        <button
          type="button"
          onClick={() => navigate(backLink)}
          className={`mb-4 inline-flex items-center ${A.label} hover:text-gray-700`}
        >
          <ArrowLeftIcon className="w-4 h-4 mr-1" />
          {t("staffAcceptInvite.backToLogin")}
        </button>

        <div className={`${A.card} p-6`}>
          <h1 className={A.title}>{t("staffAcceptInvite.title")}</h1>

          <p className={`mt-1 ${A.label}`}>
            {isExistingUser ? (
              <Trans i18nKey="staffAcceptInvite.existingDescription" values={{ companyName }} tOptions={{ interpolation: { escapeValue: true } }} shouldUnescape
                components={{ company: <span className="font-semibold text-gray-700" /> }} />
            ) : (
              <Trans i18nKey="staffAcceptInvite.newDescription" values={{ companyName }} tOptions={{ interpolation: { escapeValue: true } }} shouldUnescape
                components={{ company: <span className="font-semibold text-gray-700" /> }} />
            )}
          </p>

          <form onSubmit={handleSubmit} className="mt-4 space-y-4">
            {/* Always show email as read-only */}
            <div>
              <label className={labelCls}>{t("directory.common.email")}</label>
              <input
                type="email"
                value={invite?.email || ""}
                readOnly
                className={`${A.input} bg-gray-50 text-gray-500 cursor-not-allowed`}
              />
            </div>

            {/* For new users: full name + password */}
            {!isExistingUser && (
              <>
                <div>
                  <label className={labelCls}>{t("staffAcceptInvite.fullName")}</label>
                  <input
                    type="text"
                    required
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className={A.input}
                    placeholder={t("staffAcceptInvite.namePlaceholder")}
                  />
                </div>

                <div>
                  <label className={labelCls}>{t("staffAcceptInvite.createPassword")}</label>
                  <input
                    type="password"
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={A.input}
                    placeholder={t("staffAcceptInvite.passwordPlaceholder")}
                  />
                </div>
              </>
            )}

            {errorText && (
              <div className={`${alertBase} border-red-200 bg-red-50 text-red-700`}>
                {errorText}
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              className="w-full justify-center"
              disabled={status === "submitting"}
            >
              {status === "submitting"
                ? t("staffAcceptInvite.accepting")
                : isExistingUser
                ? t("staffAcceptInvite.acceptContinue")
                : t("staffAcceptInvite.createJoin")}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
